<template>
  <BaseBookingCard
    :disable-submit="disabled || disabledSubmitBooking"
    @submit="submitBooking()"
  >
    <p class="text-muted">{{ $t('Select your options and dates to book')}}</p>

    <div class="form-group mt-2">
      <ProductCombinationSelector
        :product-combinations="combinations"
        :selectedCombination="selectedCombination"
        @combination-change="onCombinationChange"
      ></ProductCombinationSelector>
    </div>
    <div class="form-group">
      <label for="bookingFormStart" class="font-weight-bold text-dark">{{ $t('Dates:') }}</label>
      <DatePicker
        range
        v-model="range"
        :min-date='datePickerMinDate'
        :max-date='maxDateInContext'
        :disabled-dates="disabledDates"
        :loading="loading"
        :input-class-name="dpInputClassName"
        @update-month-year="handleMonthYear"
        @first-date-selected="onFirstDateSelected"
      ></DatePicker>

      <small class="text-muted d-block mt-2">
        {{ $t('Booking start the first day at {startTime} and finish the last day at {endTime}', {startTime, endTime}) }}
      </small>
    </div>
  </BaseBookingCard>
</template>

<script>
import dayjs from 'dayjs'
import BaseBookingCard from "@/components/frontend/booking/BookingCard.vue";
import { getEquipmentAvailable, maxAvailableDate } from "@/utils/booking/equipmentAvailability.ts"
import DatePicker from "@/components/frontend/ui/DatePicker.vue";
import ProductCombinationSelector from "@/components/frontend/booking/ProductCombinationSelector.vue";

export default {
  components: {
    ProductCombinationSelector,
    DatePicker,
    BaseBookingCard,
  },
  props: {
    product: {
      type: String,
      required: true
    },
    combinations: {
      type: Array,
      required: true
    },
    urlAvailability: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    startTime: {
      type: String,
      default: '08:00'
    },
    endTime: {
      type: String,
      default: '18:00'
    },
    minDuration: {
      type: Number,
      required: true
    },
    maxDuration: {
      type: Number,
      required: true
    },
    minDate: {
      type: String,
      required: true
    },
    maxDate: {
      type: String,
    },
  },
  data() {
    return {
      range: null,
      selectedCombination: null,
      size: null,
      color: null,
      year: (new Date()).getFullYear(),
      month: (new Date()).getMonth() + 1,
      equipmentsAvailability: [],
      disabledDates: [],
      bookingRequest: {
        start_at: null,
        end_at: null,
        equipment_id: null,
      },
      disabledSubmitBooking: true,
      loading: false,
    }
  },
  computed: {
    dpInputClassName() {
      return this.range ? 'dp-valid-range' : ''
    },
    datePickerMinDate() {
      return dayjs(this.minDate).format('YYYY-MM-DD');
    },
    maxDateInContext: function () {
      let bookingMaxDate = this.maxDate ? dayjs(this.maxDate) : null;
      let availableMaxDate = null;

      if (this.bookingRequest.start_at) {
        const startAt = dayjs(this.bookingRequest.start_at);
        availableMaxDate = maxAvailableDate(
          this.equipmentsAvailability,
          startAt,
          this.maxDuration,
          this.startTime,
          this.endTime,
        )
      }

      if (!bookingMaxDate && availableMaxDate) {
        bookingMaxDate = availableMaxDate;
      } else if (availableMaxDate && bookingMaxDate && bookingMaxDate.isAfter(availableMaxDate)) {
        bookingMaxDate = availableMaxDate;
      }

      return bookingMaxDate?.format('YYYY-MM-DD');
    }
  },
  watch: {
    equipmentsAvailability: function (equipments) {
      const disabledDates = [];

      // using reduce to get all the dates blocked
      const datesBlocked = equipments.reduce((acc, equipment) => {
        return acc.concat(equipment.datesBlocked);
      }, []);

      // for each date of the current month count the number of dates blocked
      // if the number of dates blocked is equal to the number of equipments
      // then we add the date to the disabledDates array
      // @todo current month
      const currentPeriod = `${this.year}-${this.month}`;
      const daysInMonth = dayjs(`${currentPeriod}-01`).daysInMonth();
      for (let i = 1; i <= daysInMonth; i++) {
        // set date with date to check
        const date = dayjs(`${currentPeriod}-${i} ${this.startTime}`) ; // .format('YYYY-MM-DD');
        // console.log('date', date.format('YYYY-MM-DD'));
        const datesBlockedForThisDate = datesBlocked.filter((dateRangeBlocked) => {
          const intervalStartDate = dayjs(dateRangeBlocked.start_at);
          const intervalEndDate = dayjs(dateRangeBlocked.end_at);

          if (intervalStartDate.isBefore(date, 'day') && intervalEndDate.isAfter(date, 'day')) {
            return true;
          }
          if (intervalEndDate.isSame(date, 'day') && intervalEndDate.isAfter(date)) {
            return true;
          }
        });
        if (datesBlockedForThisDate.length === equipments.length) {
          disabledDates.push(date.format('YYYY-MM-DD'));
        }
      }

      this.disabledDates = disabledDates;
    },

    range: {
      handler: function (newVal) {
        if (newVal) {
          this.bookingRequest.start_at = newVal[0];
          this.bookingRequest.end_at = newVal[1]
          this.setBookingRequest();
        } else {
          this.bookingRequest.start_at = null;
          this.bookingRequest.end_at = null;
        }
      },
      deep: true
    },
    // deepwatch bookingRequest
    bookingRequest: {
      handler: function (newVal) {
        // console.log('bookingRequest', newVal)
        this.disabledSubmitBooking = !(newVal.start_at && newVal.end_at && newVal.equipment_id);
      },
      deep: true
    }
  },
  methods: {
    onFirstDateSelected(e) {
      console.log('onFirstDateSelected', e);
      this.bookingRequest.start_at = dayjs(e)
        .set('hour', this.startTime.split(':')[0])
        .set('minute', this.startTime.split(':')[1])
        .toISOString()
    },
    handleMonthYear({ month, year }) {
      this.year = year;
      this.month = month + 1;
      this.fetchAvailability();
    },
    onCombinationChange(combination) {
      console.log('onCombinationChange', combination);
      this.selectedCombination = combination;
      this.size = combination?.size;
      this.color = combination?.color;
      this.fetchAvailability();
    },
    fetchAvailability() {
      this.loading = true;

      axios.get(this.urlAvailability, {
        params: {
          product: this.product,
          size: this.size,
          color: this.color,
          year: this.year,
          month: this.month,
        }
      }).then(response => {
        this.equipmentsAvailability = response.data.data;
        this.setBookingRequest();
      }).catch(error => {
        console.error('fetchAvailability', error);
      }).finally(() => {
        this.loading = false;
      });
    },

    setBookingRequest() {
      if (!this.range) {
        return;
      }

      this.bookingRequest.equipment_id = getEquipmentAvailable(
        this.equipmentsAvailability,
        {
          start_at: dayjs(this.bookingRequest.start_at),
          end_at: dayjs(this.bookingRequest.end_at)
        }
      )?.id
    },

    submitBooking() {
      this.$emit('submit-booking', {
        start_at: dayjs(this.bookingRequest.start_at).format('YYYY-MM-DD'),
        end_at: dayjs(this.bookingRequest.end_at).format('YYYY-MM-DD'),
        equipment: this.bookingRequest.equipment_id,
      });
    }
  },
  mounted() {
    this.onCombinationChange(this.combinations[0]);
  }
}
</script>

<style>
.dp-valid-range{
  border: 2px solid #1976d2; /* same as OptionValue.vue */
  padding-top: 0.5em;
  padding-bottom: 0.5em;
}
</style>